/**
 * Bloco 5 + 6 — Buffer de behavior (snapshots + eventos críticos).
 * Limite por sessão (FIFO) para evitar crescimento indefinido de memória.
 * Estrutura final congelada: { visitorId, sessionId, behaviorSnapshots, behaviorCriticalEvents }.
 */

import type {
  BehaviorSnapshot,
  BehaviorCriticalEvent,
  BehaviorBatchPayload,
  BehaviorCriticalEventType,
} from './types';

/** Máximo de snapshots por sessão (FIFO). Sessões longas não explodem memória. */
const MAX_SNAPSHOTS = 200;
/** Máximo de eventos críticos acumulados (FIFO). */
const MAX_CRITICAL_EVENTS = 100;

const behaviorSnapshots: BehaviorSnapshot[] = [];
const behaviorCriticalEvents: BehaviorCriticalEvent[] = [];
const pendingFlushes = new Map<string, BehaviorBatchPayload>();

function isDebugPayloadSize(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!(window as unknown as { __behaviorDebug?: boolean }).__behaviorDebug
  );
}

export function addBehaviorSnapshot(snapshot: BehaviorSnapshot): void {
  if (behaviorSnapshots.length >= MAX_SNAPSHOTS) behaviorSnapshots.shift();
  behaviorSnapshots.push(snapshot);
  if (isDebugPayloadSize()) {
    const size = JSON.stringify(snapshot).length;
    const total = behaviorSnapshots.reduce(
      (acc, s) => acc + JSON.stringify(s).length,
      0
    );
    console.log(
      '[Behavior] Snapshot added:',
      size,
      'bytes (buffer total snapshots:',
      behaviorSnapshots.length,
      ', ~',
      total,
      'bytes)'
    );
  }
}

export function addBehaviorCriticalEvent(
  type: BehaviorCriticalEventType,
  visitorId: string,
  sessionId: string,
  pageId: string,
  payload?: Record<string, unknown>
): void {
  if (behaviorCriticalEvents.length >= MAX_CRITICAL_EVENTS)
    behaviorCriticalEvents.shift();
  behaviorCriticalEvents.push({
    type,
    visitorId,
    sessionId,
    pageId,
    timestamp: Date.now(),
    payload,
  });
}

/** Payload de lote (nunca misturar conversion). Para futura integração com queue. */
export function getBehaviorBatchPayload(
  visitorId: string,
  sessionId: string
): BehaviorBatchPayload {
  return {
    visitorId,
    sessionId,
    behaviorSnapshots: [...behaviorSnapshots],
    behaviorCriticalEvents: [...behaviorCriticalEvents],
  };
}

export function getBehaviorSnapshotCount(): number {
  return behaviorSnapshots.length;
}

export function getBehaviorCriticalEventCount(): number {
  return behaviorCriticalEvents.length;
}

/** Limpa buffers e pendências (ex.: nova sessão ou destroy). */
export function clearBehaviorBuffer(): void {
  behaviorSnapshots.length = 0;
  behaviorCriticalEvents.length = 0;
  pendingFlushes.clear();
}

export function takeBehaviorBufferForQueue(
  visitorId: string,
  sessionId: string
): BehaviorBatchPayload | null {
  const payload = getBehaviorBatchPayload(visitorId, sessionId);
  if (
    payload.behaviorSnapshots.length === 0 &&
    payload.behaviorCriticalEvents.length === 0
  ) {
    return null;
  }
  clearBehaviorBuffer();
  return payload;
}

export function registerPendingBehaviorFlush(
  queueItemId: string,
  payload: BehaviorBatchPayload
): void {
  pendingFlushes.set(queueItemId, payload);
}

export function commitPendingBehaviorFlush(queueItemId: string): void {
  pendingFlushes.delete(queueItemId);
}

function restorePayloadToBuffer(payload: BehaviorBatchPayload): void {
  behaviorSnapshots.unshift(...payload.behaviorSnapshots);
  behaviorCriticalEvents.unshift(...payload.behaviorCriticalEvents);
  while (behaviorSnapshots.length > MAX_SNAPSHOTS) behaviorSnapshots.pop();
  while (behaviorCriticalEvents.length > MAX_CRITICAL_EVENTS)
    behaviorCriticalEvents.pop();
}

export function rollbackPendingBehaviorFlush(queueItemId: string): void {
  const pending = pendingFlushes.get(queueItemId);
  if (!pending) return;
  restorePayloadToBuffer(pending);
  pendingFlushes.delete(queueItemId);
}

export function commitSettledBehaviorFlushes(settledIds: Set<string>): void {
  for (const id of settledIds) {
    if (pendingFlushes.has(id)) commitPendingBehaviorFlush(id);
  }
}

export function getPendingBehaviorFlushCount(): number {
  return pendingFlushes.size;
}

/** Para simulação: retorna tamanho e amostra (sem enviar). */
export function getBehaviorBufferStats(): {
  snapshotCount: number;
  criticalEventCount: number;
  lastSnapshot: BehaviorSnapshot | null;
  lastCritical: BehaviorCriticalEvent | null;
} {
  return {
    snapshotCount: behaviorSnapshots.length,
    criticalEventCount: behaviorCriticalEvents.length,
    lastSnapshot: behaviorSnapshots[behaviorSnapshots.length - 1] ?? null,
    lastCritical:
      behaviorCriticalEvents[behaviorCriticalEvents.length - 1] ?? null,
  };
}
